import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Home, Eye, ChevronLeft, User, BarChart3, RotateCcw, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { questions } from "@/data/questions";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from 'xlsx';

interface ParticipantAttempt {
  id: string;
  user_id: string;
  run_id: string;
  completed: boolean;
  overall_score: number | null;
  started_at: string;
  completed_at: string | null;
  profiles: {
    first_name: string;
    last_name: string;
    college_id?: string | null;
    college_name?: string | null;
    academic_role?: string | null;
  } | null;
  assessment_runs: {
    name: string;
  } | null;
}

interface ResponseDetail {
  question_id: string | number;
  rating: number;
  competence_area: string;
  created_at: string;
}

const AdminParticipants = () => {
  const { user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<ParticipantAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState<ParticipantAttempt | null>(null);
  const [responses, setResponses] = useState<ResponseDetail[]>([]);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [attemptToReopen, setAttemptToReopen] = useState<ParticipantAttempt | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [attemptToReset, setAttemptToReset] = useState<ParticipantAttempt | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCollege, setSelectedCollege] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  const [dbColleges, setDbColleges] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [filteredAttempts, setFilteredAttempts] = useState<ParticipantAttempt[]>([]);
  const [selectedRun, setSelectedRun] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");


  useEffect(() => {
    if (!isLoading && (!user || !isAdmin)) {
      navigate("/");
    }
  }, [user, isAdmin, isLoading, navigate]);

  useEffect(() => {
    if (user && isAdmin) {
      fetchAllAttempts();
      fetchColleges();
      fetchRuns();
    }
  }, [user, isAdmin]);

  const fetchColleges = async () => {
    const { data, error } = await supabase.from("colleges").select("id, name");
    if (data && !error) {
      setDbColleges(data);
    }
  };

  const fetchRuns = async () => {
    const { data, error } = await supabase.from("assessment_runs").select("id, name");
    if (data && !error) {
      setRuns(data);
    }
  };

  const fetchAllAttempts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("attempts")
      .select("*")
      .order("started_at", { ascending: false });

    if (data && !error) {
      const attemptsWithDetails = await Promise.all(
        data.map(async (attempt) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name, college_id, college_name, academic_role")
            .eq("id", attempt.user_id)
            .maybeSingle();

          const { data: run } = await supabase
            .from("assessment_runs")
            .select("name")
            .eq("id", attempt.run_id)
            .maybeSingle();

          return {
            ...attempt,
            profiles: profile ? {
              first_name: (profile as any).first_name || 'Unknown',
              last_name: (profile as any).last_name || 'User',
              college_id: (profile as any).college_id,
              college_name: (profile as any).college_name,
              academic_role: (profile as any).academic_role
            } : {
              first_name: 'Unknown',
              last_name: 'User',
              college_id: null,
              college_name: null,
              academic_role: undefined
            },
            assessment_runs: run ? {
              name: run.name || 'N/A'
            } : {
              name: 'N/A'
            },
          };
        })
      );
      setAttempts(attemptsWithDetails);
    }
    setLoading(false);
  };

  // Filter Logic
  useEffect(() => {
    let filtered = [...attempts];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(attempt =>
      (attempt.profiles?.first_name?.toLowerCase().includes(query) ||
        attempt.profiles?.last_name?.toLowerCase().includes(query))
      );
    }

    if (selectedCollege !== "all") {
      filtered = filtered.filter(attempt => {
        // 1. Resolve User's College Name (Robust Fallback)
        // If they have an ID, try to find the name. If ID is invalid/not found, dbName is undefined.
        // Fallback to college_name property.
        const dbName = attempt.profiles?.college_id
          ? dbColleges.find(c => c.id === attempt.profiles?.college_id)?.name
          : null;
        const userCollegeName = dbName || attempt.profiles?.college_name;

        // 2. Resolve Selected Filter Name
        // The selectedCollege value might be a UUID (from dbColleges) or a raw name (from custom list)
        const selectedDbCollege = dbColleges.find(c => c.id === selectedCollege);
        const selectedFilterName = selectedDbCollege ? selectedDbCollege.name : selectedCollege;

        // 3. Match Logic
        // Case A: Exact ID match (Official Link)
        if (attempt.profiles?.college_id === selectedCollege) return true;

        // Case B: Name match (Unlinked profiles or Custom entries)
        // We compare normalized names to be safe, though exact match is usually expected regarding case sensitivity in Select
        if (userCollegeName && selectedFilterName && userCollegeName === selectedFilterName) return true;

        return false;
      });
    }

    if (selectedRole !== "all") {
      filtered = filtered.filter(attempt =>
        attempt.profiles?.academic_role === selectedRole
      );
    }

    if (selectedRun !== "all") {
      filtered = filtered.filter(attempt =>
        attempt.run_id === selectedRun
      );
    }

    if (selectedStatus !== "all") {
      filtered = filtered.filter(attempt =>
        selectedStatus === "completed" ? attempt.completed : !attempt.completed
      );
    }

    setFilteredAttempts(filtered);
    setCurrentPage(1);
  }, [attempts, searchQuery, selectedCollege, selectedRole, selectedRun, selectedStatus, dbColleges]);

  const viewResponses = async (attempt: ParticipantAttempt) => {
    setSelectedAttempt(attempt);
    const { data } = await supabase
      .from("responses")
      .select("*")
      .eq("attempt_id", attempt.id)
      .order("created_at", { ascending: true });

    if (data) {
      setResponses(data);
    }
    setShowResponseDialog(true);
  };

  const getScoreColor = (score: number | null) => {
    if (!score) return "text-muted-foreground";
    if (score >= 80) return "text-success";
    if (score >= 60) return "text-primary";
    if (score >= 40) return "text-accent";
    return "text-muted-foreground";
  };

  const handleReopenAssessment = async () => {
    if (!attemptToReopen) return;

    try {
      // First, explicitly delete all responses for this attempt
      const { error: responsesError } = await supabase
        .from("responses")
        .delete()
        .eq("attempt_id", attemptToReopen.id);

      if (responsesError) {
        console.error('Error deleting responses:', responsesError);
        // Continue anyway, as cascade should handle it
      }

      // Then delete the attempt
      const { error } = await supabase
        .from("attempts")
        .delete()
        .eq("id", attemptToReopen.id);

      if (error) {
        toast({
          title: "Error",
          description: `Failed to reopen assessment: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: `Assessment reopened for ${attemptToReopen.profiles?.first_name} ${attemptToReopen.profiles?.last_name}. All previous responses have been deleted.`,
      });

      // Refresh the attempts list
      await fetchAllAttempts();
      setShowReopenDialog(false);
      setAttemptToReopen(null);
    } catch (err) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleResetAssessment = async () => {
    if (!attemptToReset) return;

    setResetLoading(true);
    try {
      const response = await fetch("/auth/ldap/admin/reset-user-assessment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: attemptToReset.user_id,
          attemptId: attemptToReset.id
        }),
      });

      const data = await response.json();

      if (!data.success) {
        toast({
          title: "Error",
          description: `Failed to reset assessment: ${data.message}`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: `Assessment reset for ${attemptToReset.profiles?.first_name} ${attemptToReset.profiles?.last_name}. All responses have been deleted and the assessment is now incomplete.`,
      });

      // Refresh the attempts list
      await fetchAllAttempts();
      setShowResetDialog(false);
      setAttemptToReset(null);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to reset assessment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Participants Summary
      const summaryData = [
        ['Participant Name', 'Assessment Run', 'College', 'Role', 'Status', 'Overall Score (%)', 'Started Date', 'Completed Date']
      ];

      const dataToExport = filteredAttempts.map(attempt => {
        const collegeName = attempt.profiles?.college_id
          ? dbColleges.find(c => c.id === attempt.profiles.college_id)?.name || attempt.profiles?.college_name || 'N/A'
          : attempt.profiles?.college_name || 'N/A';

        return [
          `${attempt.profiles?.first_name || ''} ${attempt.profiles?.last_name || ''}`.trim(),
          attempt.assessment_runs?.name || 'N/A',
          collegeName,
          attempt.profiles?.academic_role || 'N/A',
          attempt.completed ? 'Completed' : 'In Progress',
          attempt.overall_score || '-',
          new Date(attempt.started_at).toLocaleDateString(),
          attempt.completed_at ? new Date(attempt.completed_at).toLocaleDateString() : '-'
        ];
      });

      summaryData.push(...dataToExport);
      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Participants Summary');

      // Sheet 2: Detailed Responses (for completed attempts)
      const completedAttempts = filteredAttempts.filter(a => a.completed);

      if (completedAttempts.length > 0) {
        const detailedData = [
          ['Participant', 'Assessment Run', 'College', 'Role', 'Question ID', 'Competence Area', 'Rating']
        ];

        for (const attempt of completedAttempts) {
          const { data: attemptResponses } = await supabase
            .from('responses')
            .select('question_id, rating, competence_area')
            .eq('attempt_id', attempt.id)
            .order('question_id');

          if (attemptResponses) {
            const participantName = `${attempt.profiles?.first_name || ''} ${attempt.profiles?.last_name || ''}`.trim();
            const collegeName = attempt.profiles?.college_id
              ? dbColleges.find(c => c.id === attempt.profiles.college_id)?.name || attempt.profiles?.college_name || 'N/A'
              : attempt.profiles?.college_name || 'N/A';

            attemptResponses.forEach(response => {
              detailedData.push([
                participantName,
                attempt.assessment_runs?.name || 'N/A',
                collegeName,
                attempt.profiles?.academic_role || 'N/A',
                response.question_id,
                response.competence_area,
                response.rating
              ]);
            });
          }
        }

        const ws2 = XLSX.utils.aoa_to_sheet(detailedData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Detailed Responses');
      }

      // Sheet 3: Statistics
      const statsData = [
        ['Metric', 'Value'],
        ['Total Participants', filteredAttempts.length],
        ['Completed Assessments', filteredAttempts.filter(a => a.completed).length],
        ['In Progress', filteredAttempts.filter(a => !a.completed).length],
        ['Average Score', filteredAttempts.filter(a => a.overall_score !== null).length > 0
          ? Math.round(filteredAttempts.filter(a => a.overall_score !== null).reduce((sum, a) => sum + (a.overall_score || 0), 0) / filteredAttempts.filter(a => a.overall_score !== null).length) + '%'
          : 'N/A'
        ],
        [''],
        ['Filters Applied:'],
        ['College', selectedCollege === 'all'
          ? 'All Colleges'
          : dbColleges.find(c => c.id === selectedCollege)?.name || selectedCollege || 'N/A'],
        ['Assessment Run', selectedRun === 'all' ? 'All Runs' : runs.find(r => r.id === selectedRun)?.name || 'N/A'],
        ['Status', selectedStatus === 'all' ? 'All Statuses' : selectedStatus],
        ['Role', selectedRole === 'all' ? 'All Roles' : selectedRole],
        ['Search Query', searchQuery || 'None']
      ];

      const ws3 = XLSX.utils.aoa_to_sheet(statsData);
      XLSX.utils.book_append_sheet(wb, ws3, 'Statistics');

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `DigComp_Participants_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast({
        title: "Success",
        description: `Exported ${filteredAttempts.length} participant records successfully!`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Error",
        description: "Failed to export data to Excel",
        variant: "destructive",
      });
    }
  };

  const totalPages = Math.ceil(filteredAttempts.length / itemsPerPage);
  const currentAttempts = filteredAttempts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Bilingual Instructions */}
        <Card className="p-8 mb-6 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/30 shadow-lg">
          <div className="space-y-3">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              <strong className="text-primary text-xl md:text-2xl">Instructions:</strong> View and navigate all participants' assessment responses. Click on the "View Responses" button to see detailed answers for each participant.
            </p>
            <p className="text-lg md:text-xl font-semibold text-foreground font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-xl md:text-2xl">التعليمات:</strong> عرض والتنقل بين إجابات جميع المشاركين في التقييم. انقر على زر "عرض الإجابات" لرؤية الإجابات التفصيلية لكل مشارك.
            </p>
          </div>
        </Card>

        <div className="flex flex-col gap-6 mb-8">
          {/* Row 1: Title and Description */}
          <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
            <h1 className="text-4xl font-bold text-foreground">All Participants</h1>
            <p className="text-muted-foreground text-lg">
              View and manage all participant responses
            </p>
          </div>

          {/* Row 2: Filters */}
          <div className="flex flex-wrap gap-3 items-center bg-card/50 p-4 rounded-lg border border-border">
            <div className="w-full md:w-[250px]">
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="w-full md:w-[200px]">
              <Select value={selectedCollege} onValueChange={setSelectedCollege}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by College" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Colleges</SelectItem>
                  {dbColleges.map((college) => (
                    <SelectItem key={college.id} value={college.id}>
                      {college.name}
                    </SelectItem>
                  ))}
                  {attempts
                    .map(a => a.profiles?.college_name)
                    .filter((name, index, self) =>
                      name &&
                      name.trim() !== '' &&
                      self.indexOf(name) === index &&
                      !dbColleges.some(c => c.name === name)
                    )
                    .map((name) => (
                      <SelectItem key={name!} value={name!}>
                        {name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-[150px]">
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="student">Students</SelectItem>
                  <SelectItem value="faculty">Faculty</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-[200px]">
              <Select value={selectedRun} onValueChange={setSelectedRun}>
                <SelectTrigger>
                  <SelectValue placeholder="Assessment Run" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Runs</SelectItem>
                  {runs.map((run) => (
                    <SelectItem key={run.id} value={run.id}>
                      {run.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-[150px]">
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="inprogress">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Actions */}
          <div className="flex flex-wrap gap-3 justify-end items-center">
            <Button
              onClick={exportToExcel}
              className="gap-2"
            >
              <FileDown className="h-4 w-4" />
              Export to Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/analytics")}
              className="gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              Analytics
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin")}
              className="gap-2"
            >
              Admin Panel
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <Home className="h-4 w-4" />
              Home
            </Button>
          </div>
        </div>

        <Card className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Participant</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>College</TableHead>
                <TableHead>Assessment Run</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Started At</TableHead>
                <TableHead>Completed At</TableHead>
                <TableHead className="text-center">Responses</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentAttempts.map((attempt) => (
                <TableRow key={attempt.id}>
                  <TableCell className="font-medium">
                    {attempt.profiles
                      ? `${attempt.profiles.first_name} ${attempt.profiles.last_name}`
                      : "Unknown User"}
                  </TableCell>
                  <TableCell>
                    <span className="capitalize">
                      {attempt.profiles?.academic_role || "N/A"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {attempt.profiles?.college_id
                      ? dbColleges.find(c => c.id === attempt.profiles?.college_id)?.name || attempt.profiles?.college_name || "N/A"
                      : attempt.profiles?.college_name || "N/A"}
                  </TableCell>
                  <TableCell>{attempt.assessment_runs?.name || "N/A"}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${attempt.completed
                        ? "bg-success/20 text-success"
                        : "bg-accent/20 text-accent"
                        }`}
                    >
                      {attempt.completed ? "Completed" : "In Progress"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`font-bold ${getScoreColor(attempt.overall_score)}`}>
                      {attempt.overall_score !== null ? `${attempt.overall_score}%` : "N/A"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {new Date(attempt.started_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                  <TableCell>
                    {attempt.completed_at
                      ? new Date(attempt.completed_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                      : "N/A"}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewResponses(attempt)}
                      className="gap-2"
                      disabled={!attempt.completed}
                    >
                      <Eye className="h-4 w-4" />
                      View Responses
                    </Button>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex gap-2 justify-center flex-wrap">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => navigate(`/admin/user-dashboard/${attempt.user_id}`)}
                        className="gap-1"
                      >
                        <User className="h-4 w-4" />
                        Dashboard
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/admin/user-analytics/${attempt.user_id}`)}
                        className="gap-1"
                      >
                        <BarChart3 className="h-4 w-4" />
                        Analytics
                      </Button>
                      {/* Reset button hidden */}
                      {/* <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setAttemptToReset(attempt);
                          setShowResetDialog(true);
                        }}
                        className="gap-1"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Reset
                      </Button> */}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredAttempts.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No participant responses found.
            </div>
          )}
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={currentPage === page}
                      onClick={() => setCurrentPage(page)}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}


        {/* Reopen Assessment Confirmation Dialog */}
        <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reopen Assessment?</DialogTitle>
              <DialogDescription>
                This will permanently delete the assessment data for{" "}
                <strong>
                  {attemptToReopen?.profiles?.first_name} {attemptToReopen?.profiles?.last_name}
                </strong>
                .
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <p className="text-sm text-muted-foreground">
                The following will be deleted:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Assessment attempt ({attemptToReopen?.assessment_runs?.name})</li>
                <li>All responses ({attemptToReopen?.completed ? "Completed" : "In Progress"})</li>
                {attemptToReopen?.overall_score && (
                  <li>Score: {attemptToReopen.overall_score}%</li>
                )}
              </ul>
              <p className="text-sm font-semibold text-foreground mt-4">
                The user's profile and login will remain intact. They can start a new assessment.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowReopenDialog(false);
                  setAttemptToReopen(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReopenAssessment}>
                Reopen Assessment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Assessment Confirmation Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Assessment?</DialogTitle>
              <DialogDescription>
                This will reset the assessment for{" "}
                <strong>
                  {attemptToReset?.profiles?.first_name} {attemptToReset?.profiles?.last_name}
                </strong>
                .
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <p className="text-sm text-muted-foreground">
                The following will be reset:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>All responses will be deleted</li>
                <li>Assessment status will be set to incomplete</li>
                <li>Score will be cleared</li>
              </ul>
              <p className="text-sm font-semibold text-foreground mt-4">
                The user will be able to resume their attempt and update their responses.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetDialog(false);
                  setAttemptToReset(null);
                }}
                disabled={resetLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleResetAssessment}
                disabled={resetLoading}
              >
                {resetLoading ? "Resetting..." : "Reset Assessment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Response Details Dialog */}
        <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                Participant Responses
              </DialogTitle>
              {selectedAttempt && (
                <div className="text-sm text-muted-foreground">
                  {selectedAttempt.profiles
                    ? `${selectedAttempt.profiles.first_name} ${selectedAttempt.profiles.last_name}`
                    : "Unknown User"}{" "}
                  - {selectedAttempt.assessment_runs?.name || "N/A"}
                </div>
              )}
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {responses.map((response) => {
                const question = questions.find((q) => q.id.toString() === response.question_id.toString());
                return (
                  <Card key={response.question_id} className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-foreground mb-1">
                            {question?.question || response.question_id}
                          </p>
                          {question?.questionAr && (
                            <p className="text-sm text-muted-foreground font-tajawal" dir="rtl">
                              {question.questionAr}
                            </p>
                          )}
                        </div>
                        <div className="ml-4">
                          <span className="px-3 py-1 bg-primary/20 text-primary rounded-full font-bold">
                            {response.rating}/8
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-semibold">Area:</span> {response.competence_area}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminParticipants;
